/**
 * One company's books: every read the ledger screens share. Changes (set-up,
 * accounts, drafts, posting, periods) live beside the screens that make them and
 * invalidate `qk.accounting.ledger`, the prefix of every key here.
 */
/* eslint-disable @linkhr/linkhr/no-raw-tanstack-query -- the accounting app keeps its own hooks (it may not import the HR ones); keys come from qk */
import {
  useInfiniteQuery,
  useQuery,
  type InfiniteData,
  type UseInfiniteQueryResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { qk, useApiClient, type ApiError, type components } from "@linkhr/api-client/core";
import { useAuth } from "@linkhr/auth/react";
import { COMPANY, answer, type Untyped } from "./untyped";

type S = components["schemas"];
export type CompanyBooks = S["AcctCompanyBooks"];
export type Account = S["AcctAccountView"];
/** A default account (an account role) with its rows, history included. */
export type RoleMapping = S["AcctRoleMappingView"];
export type RoleMappingRow = S["AcctRoleMappingRow"];
export type InventoryCountResult = S["AcctInventoryCountResult"];
export type Journal = S["AcctJournalView"];
export type Contact = S["AcctContactView"];
export type Branch = S["AcctBranchView"];
export type CostCentre = S["AcctCostCentreView"];
export type FiscalYear = S["AcctFiscalYear"];
export type Periods = S["AcctPeriods"];
export type LockException = S["AcctLockExceptionView"];
export type FxRate = S["AcctFxRateView"];
export type DraftSummary = S["AcctDraftSummary"];
export type Draft = S["AcctDraft"];
export type Entry = S["AcctEntry"];
export type PeriodSummary = S["AcctPeriodSummary"];
export type CashByMonth = S["AcctCashByMonth"];
export type Balances = S["AcctBalances"];
export type TrialBalance = S["AcctTrialBalance"];
export type GeneralLedger = S["AcctGeneralLedger"];
export type OpenItems = S["AcctOpenItems"];

type Query = Record<string, string | number | boolean | undefined>;

/** Drops unset values so they neither reach the server nor split the cache. */
function defined(q: Query): Record<string, string | number | boolean> {
  return Object.fromEntries(
    Object.entries(q).filter(([, v]) => v !== undefined && v !== ""),
  ) as Record<string, string | number | boolean>;
}

/** A single ledger read: `path` after the company, its query, and whether to ask yet. */
export function useLedgerRead<T>(
  companyId: string,
  resource: string,
  path: string,
  query: Query = {},
  opts: { enabled?: boolean; pathParams?: Record<string, string>; sideScoped?: boolean } = {},
): UseQueryResult<T, ApiError> {
  const client = useApiClient() as unknown as Untyped;
  const { tenantId } = useAuth();
  const params = defined(query);
  return useQuery<T, ApiError>({
    queryKey: qk.accounting.ledgerRead(tenantId ?? "", companyId, resource, {
      ...params,
      ...opts.pathParams,
    }),
    queryFn: () =>
      answer<T>(
        client.GET(`${COMPANY}${path}`, {
          params: { path: { companyId, ...opts.pathParams }, query: params },
        }),
      ),
    enabled: opts.enabled ?? true,
    ...(opts.sideScoped === true ? { meta: { sideScoped: true } } : {}),
  });
}

/** A cursor-paged ledger read (`after` / `nextCursor`). */
export function useLedgerPages<T extends { nextCursor?: string | null }>(
  companyId: string,
  resource: string,
  path: string,
  query: Query,
  opts: { enabled?: boolean; pathParams?: Record<string, string>; sideScoped?: boolean } = {},
): UseInfiniteQueryResult<InfiniteData<T, string | null>, ApiError> {
  const client = useApiClient() as unknown as Untyped;
  const { tenantId } = useAuth();
  const params = defined(query);
  return useInfiniteQuery<
    T,
    ApiError,
    InfiniteData<T, string | null>,
    readonly unknown[],
    string | null
  >({
    queryKey: qk.accounting.ledgerRead(tenantId ?? "", companyId, resource, {
      ...params,
      ...opts.pathParams,
    }),
    initialPageParam: null,
    queryFn: ({ pageParam }) =>
      answer<T>(
        client.GET(`${COMPANY}${path}`, {
          params: {
            path: { companyId, ...opts.pathParams },
            query: { ...params, ...(pageParam !== null ? { after: pageParam } : {}) },
          },
        }),
      ),
    getNextPageParam: (last) => last.nextCursor ?? null,
    enabled: opts.enabled ?? true,
    ...(opts.sideScoped === true ? { meta: { sideScoped: true } } : {}),
  });
}

// ---- Company and reference reads ------------------------------------------------

export const useCompanyBooks = (companyId: string): UseQueryResult<CompanyBooks, ApiError> =>
  useLedgerRead(companyId, "books", "");
export const useAccounts = (companyId: string): UseQueryResult<Account[], ApiError> =>
  useLedgerRead(companyId, "accounts", "/accounts");
/** Every default account of the catalogue, by role, with its rows. */
export const useRoleMappings = (
  companyId: string,
  enabled = true,
): UseQueryResult<RoleMapping[], ApiError> =>
  useLedgerRead(companyId, "role-mappings", "/role-mappings", {}, { enabled });
export const useJournals = (companyId: string): UseQueryResult<Journal[], ApiError> =>
  useLedgerRead(companyId, "journals", "/journals");
export const useContacts = (
  companyId: string,
  query: { kind?: string; q?: string } = {},
): UseQueryResult<Contact[], ApiError> => useLedgerRead(companyId, "contacts", "/contacts", query);
export const useCompanyBranches = (companyId: string): UseQueryResult<Branch[], ApiError> =>
  useLedgerRead(companyId, "branches", "/branches");
export const useCostCentres = (companyId: string): UseQueryResult<CostCentre[], ApiError> =>
  useLedgerRead(companyId, "cost-centres", "/cost-centres");
export const useFiscalYears = (companyId: string): UseQueryResult<FiscalYear[], ApiError> =>
  useLedgerRead(companyId, "fiscal-years", "/fiscal-years");
export const usePeriods = (
  companyId: string,
  fiscalYearId?: string,
  enabled = true,
): UseQueryResult<Periods, ApiError> =>
  useLedgerRead(companyId, "periods", "/periods", { fiscalYearId }, { enabled });
export const useLockExceptions = (
  companyId: string,
  periodId: string,
  active?: boolean,
): UseQueryResult<LockException[], ApiError> =>
  useLedgerRead(
    companyId,
    "lock-exceptions",
    "/periods/{periodId}/lock-exceptions",
    { active },
    { pathParams: { periodId } },
  );
export const useFxRates = (
  companyId: string,
  query: { currency: string; from: string; to: string },
  enabled = true,
): UseQueryResult<FxRate[], ApiError> =>
  useLedgerRead(companyId, "fx-rates", "/fx-rates", query, { enabled });

// ---- Journals and entries --------------------------------------------------------

export const useJournalDrafts = (
  companyId: string,
  query: { kind?: string; state?: string } = {},
): UseQueryResult<DraftSummary[], ApiError> =>
  useLedgerRead(companyId, "drafts", "/journal-entries", query);
export const useJournalDraft = (
  companyId: string,
  draftId: string | undefined,
): UseQueryResult<Draft, ApiError> =>
  useLedgerRead(
    companyId,
    "draft",
    "/journal-entries/{draftId}",
    {},
    { enabled: draftId !== undefined, pathParams: { draftId: draftId ?? "" } },
  );
export const useEntry = (companyId: string, entryId: string): UseQueryResult<Entry, ApiError> =>
  useLedgerRead(companyId, "entry", "/entries/{entryId}", {}, { pathParams: { entryId } });

// ---- Reports ---------------------------------------------------------------------

export const usePeriodSummary = (
  companyId: string,
  query: { from: string; to: string },
  enabled = true,
): UseQueryResult<PeriodSummary, ApiError> =>
  useLedgerRead(companyId, "period-summary", "/reports/period-summary", query, { enabled });
export const useCashByMonth = (
  companyId: string,
  // Months, not dates: the server reads `YYYY-MM` (a full date answers 400).
  query: { from: string; to: string },
  enabled = true,
): UseQueryResult<CashByMonth, ApiError> =>
  useLedgerRead(companyId, "cash-by-month", "/reports/cash-by-month", query, { enabled });
export const useBalances = (
  companyId: string,
  query: { asOf?: string; area?: string; accountId?: string } = {},
  enabled = true,
): UseQueryResult<Balances, ApiError> =>
  useLedgerRead(companyId, "balances", "/reports/balances", query, { enabled });
export const useTrialBalance = (
  companyId: string,
  query: { from: string; to: string; compareTo?: string },
  enabled = true,
): UseQueryResult<TrialBalance, ApiError> =>
  useLedgerRead(companyId, "trial-balance", "/reports/trial-balance", query, { enabled });
export const useGeneralLedger = (
  companyId: string,
  query: { scope: string; accountId?: string; from: string; to: string; limit?: number },
  enabled = true,
): UseInfiniteQueryResult<InfiniteData<GeneralLedger, string | null>, ApiError> =>
  useLedgerPages(companyId, "general-ledger", "/reports/general-ledger", query, { enabled });
/** A side the reader may not see answers 403 (ACCESS_DENIED, or COMPANY_ACCESS_REVOKED) for that side only. */
export const useOpenItems = (
  companyId: string,
  query: { side: string; asOf?: string; contactId?: string; dueSoonDays?: number; limit?: number },
  enabled = true,
): UseInfiniteQueryResult<InfiniteData<OpenItems, string | null>, ApiError> =>
  useLedgerPages(companyId, "open-items", "/open-items", query, { enabled, sideScoped: true });
